import React, { useMemo, useState } from 'react';
import { Confirm } from '../../../userManagement/components/Confirm';
import { toAssignmentViews } from './assignmentUtils';
import { ADD_ROLE_LABEL, REMOVE_ROLE_CONFIRM } from './constants';
import { PermissionList } from './PermissionList';
import {
  AddRoleButton,
  EmptyRolesText,
  EmptyRolesWrap,
  PermissionsTitle,
  RemoveRoleButton,
  RoleTab,
  RoleTabs,
  RolesCard,
  TabContentHeader,
  TabsRow,
} from './styles';
import { IdentityRolesCardProps } from './types';
import { useActiveAssignmentTab } from './useActiveAssignmentTab';

interface EmptyRolesPlaceholderProps {
  onAddRole: () => void;
}

function EmptyRolesPlaceholder({ onAddRole }: EmptyRolesPlaceholderProps) {
  return (
    <EmptyRolesWrap>
      <EmptyRolesText>
        No roles have been assigned to this identity yet.
      </EmptyRolesText>
      <AddRoleButton color="primary" onClick={onAddRole}>
        {ADD_ROLE_LABEL}
      </AddRoleButton>
    </EmptyRolesWrap>
  );
}

export function IdentityRolesCard({
  assignments,
  onAddRole,
  onRemoveRole,
}: IdentityRolesCardProps) {
  const views = useMemo(() => toAssignmentViews(assignments), [assignments]);
  const { activeId, setActiveId } = useActiveAssignmentTab(views);
  const [isRemoveOpen, setIsRemoveOpen] = useState(false);
  const activeView = views.find(view => view.id === activeId) ?? views[0];

  function handleConfirmRemove() {
    if (!activeView) return;
    onRemoveRole(activeView.id);
    setIsRemoveOpen(false);
  }

  if (!activeView) {
    return (
      <RolesCard elevation={0}>
        <EmptyRolesPlaceholder onAddRole={onAddRole} />
      </RolesCard>
    );
  }

  return (
    <RolesCard elevation={0}>
      <TabsRow>
        <RoleTabs
          indicatorColor="primary"
          scrollButtons="off"
          textColor="primary"
          value={activeView.id}
          variant="scrollable"
          onChange={(_event, value: string) => setActiveId(value)}
        >
          {views.map(view => (
            <RoleTab key={view.id} label={view.label} value={view.id} />
          ))}
        </RoleTabs>
        <AddRoleButton color="primary" onClick={onAddRole}>
          {ADD_ROLE_LABEL}
        </AddRoleButton>
      </TabsRow>
      <TabContentHeader>
        <PermissionsTitle>Permissions</PermissionsTitle>
        <RemoveRoleButton onClick={() => setIsRemoveOpen(true)}>
          Remove role
        </RemoveRoleButton>
      </TabContentHeader>
      <PermissionList permissions={activeView.permissions} />
      <Confirm
        isOpen={isRemoveOpen}
        title={REMOVE_ROLE_CONFIRM.title}
        content={REMOVE_ROLE_CONFIRM.content}
        confirm={REMOVE_ROLE_CONFIRM.confirm}
        cancel={REMOVE_ROLE_CONFIRM.cancel}
        onConfirm={handleConfirmRemove}
        onClose={() => setIsRemoveOpen(false)}
      />
    </RolesCard>
  );
}
